import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

const STORAGE_KEY = "sailkha_cookie_consent";

// Feature-detect localStorage. Private mode (older Safari), disabled
// storage, and quota errors all need to fail gracefully.
function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    const s = window.localStorage;
    const probe = "__sailkha_probe__";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function readStoredConsent(storage: Storage | null): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Defer slightly so the banner doesn't fight the page loader.
    const t = setTimeout(() => {
      const storage = getStorage();
      if (!storage) {
        // Storage unavailable (private mode / blocked). Show the banner
        // once per page load but never block the user — dismissing it
        // simply hides it for this session.
        setIsVisible(true);
        return;
      }
      const stored = readStoredConsent(storage);
      if (!stored) setIsVisible(true);
    }, 800);
    const reopen = () => setIsVisible(true);
    window.addEventListener("sailkha:open-cookie-consent", reopen);
    return () => {
      clearTimeout(t);
      window.removeEventListener("sailkha:open-cookie-consent", reopen);
    };
  }, []);

  const persist = (value: "accepted" | "declined") => {
    const storage = getStorage();
    if (storage) {
      try {
        storage.setItem(
          STORAGE_KEY,
          JSON.stringify({ value, at: new Date().toISOString() })
        );
      } catch {
        /* quota / privacy mode — choice still applied for this session */
      }
    }
    if (value === "accepted") analytics.cookieAccept();
    else analytics.cookieDecline();
    // Legacy custom event kept for any existing listeners
    try {
      window.dispatchEvent(
        new CustomEvent("sailkha:cookie-consent-action", {
          detail: { action: value, at: new Date().toISOString() },
        })
      );
    } catch {
      /* ignore */
    }
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 sm:pb-6 pointer-events-none"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="container mx-auto pointer-events-auto">
            <div className="bg-foreground text-background border border-background/10 shadow-2xl">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6 p-5 sm:p-6 lg:p-7 relative">
                <button
                  onClick={() => persist("declined")}
                  className="lg:hidden absolute top-3 right-3 p-1.5 text-background/70 hover:text-background transition-colors"
                  aria-label="Dismiss cookie banner"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className="flex-1 pr-8 lg:pr-0">
                  <p className="text-xs tracking-wide-elegant uppercase text-background/65 mb-2">
                    Cookies
                  </p>
                  <p className="text-sm text-background/80 leading-relaxed max-w-2xl">
                    We use a minimal set of cookies to keep this site running smoothly.
                    By continuing to browse, you agree to our use of cookies. Read our{" "}
                    <Link
                      to="/privacy"
                      onClick={() => analytics.cookiePrivacyClick()}
                      className="underline underline-offset-4 text-background hover:text-background/80 transition-colors"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => persist("declined")}
                    className="rounded-none h-10 px-5 text-xs tracking-wide-elegant uppercase bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background"
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={() => persist("accepted")}
                    className="rounded-none h-10 px-6 text-xs tracking-wide-elegant uppercase bg-background text-foreground hover:bg-background/90"
                  >
                    Accept
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
